import type { SupabaseClient } from "@supabase/supabase-js";
import { ScheduleConflictError, isScheduleConflict } from "@produce/shared";

// Extracted from CreateEditBriefing.tsx so the save logic is unit-
// testable and so the DELETE-then-INSERT atomicity gap is replaced
// with a single RPC call. The lineup is the producer's most-edited
// resource and the least forgiving of data loss — mid-save failure
// must never wipe it.

export interface LineupRow {
  /**
   * Existing lineup.id when this row corresponds to a pre-existing
   * lineup entry. The replace_lineup RPC v3 (migration
   * 20260504240600) UPDATEs in place when this is set — preserves
   * the lineup.id across edits so crew_assignments.lineup_id FK
   * pointers stay valid through artist renames. Omit (or null) for
   * brand-new rows.
   */
  id?: string;
  briefing_id: string;
  stage_name: string;
  stage_id: string | null;
  event_date: string;
  artist_name: string;
  set_start: string;
  set_end: string;
  /**
   * Producer "highlight" flag. Optional so older callers/payloads
   * remain valid; replace_lineup defaults a missing value to false.
   */
  highlighted?: boolean;
}

/**
 * Atomically replace the entire lineup for a briefing via the
 * replace_lineup RPC. Server-side does DELETE + INSERT in one
 * transaction, so the prior lineup is preserved if any row in the
 * new payload violates a constraint.
 *
 * With `expectedScheduleRevision` (the revision the editor's schedule
 * check passed, lib/saveFlow/scheduleRevision.ts) it writes through
 * replace_lineup_at_revision instead: refused, with nothing written, when a
 * phone edit moved the revision since — thrown as ScheduleConflictError.
 * null writes unchecked, as before.
 */
export async function replaceLineup(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  client: SupabaseClient<any, any, any>,
  briefingId: string,
  rows: LineupRow[],
  expectedScheduleRevision: number | null = null,
): Promise<void> {
  const { error } =
    expectedScheduleRevision === null
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (client.rpc as any)("replace_lineup", { p_briefing_id: briefingId, p_rows: rows })
      : // eslint-disable-next-line @typescript-eslint/no-explicit-any
        await (client.rpc as any)("replace_lineup_at_revision", {
          p_briefing_id: briefingId,
          p_rows: rows,
          p_expected_schedule_revision: expectedScheduleRevision,
        });
  if (error) {
    if (isScheduleConflict(error)) throw new ScheduleConflictError(error.message);
    throw new Error(`replace_lineup failed: ${error.message}`);
  }
}
